import {
  Button,
  Form,
  FormProps,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
} from "antd";
import { FC, useEffect } from "react";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import {
  useCreateVessel,
  useGetVesselById,
  useGetVesselTypes,
  useUpdateVessel,
  VESSEL_KEYS,
} from "~/services/vessel";
import { capitalize } from "~/utils";
import { Maintenance, VesselStatus } from "~/utils/constants";
import { useVesselStore } from "../store/vessel.store";
import { useQueryClient } from "@tanstack/react-query";

type Props = {};

export const VesselModal: FC<Props> = () => {
  const [form] = Form.useForm();

  const qc = useQueryClient();

  const {
    formState: { open, vesselId },
    filters,
    closeVesselModal,
  } = useVesselStore();
  const { success, error } = useNotification();

  const { data: vesselTypes } = useGetVesselTypes();

  const createMutation = useCreateVessel();
  const updateMutation = useUpdateVessel();

  const { data: vesselData, isLoading: isLoadingVessel } =
    useGetVesselById(vesselId);

  useEffect(() => {
    if (!vesselData) return;
    form.setFieldsValue({
      ...vesselData,
    });
  }, [form, vesselData]);

  const onFinish: FormProps["onFinish"] = (values) => {
    const formattedValues = {
      ...values,
      boxLength: Number(values.boxLength || 0),
      doorWidth: Number(values.doorWidth || 0),
      doorHeight: Number(values.doorHeight || 0),
      internalHeight: Number(values.internalHeight || 0),
      overallLength: Number(values.overallLength || 0),
      overallHeight: Number(values.overallHeight || 0),
      hourlyRate: Number(values.hourlyRate || 0),
      directCost: Number(values.directCost || 0),
    };

    const mutation = vesselId ? updateMutation : createMutation;
    const payload = vesselId
      ? { id: vesselId, ...formattedValues }
      : formattedValues;

    mutation.mutate(payload, {
      onSuccess: () => {
        success({
          message: vesselId
            ? "Vessel updated successfully"
            : "Vessel created successfully",
        });
        closeVesselModal();
        qc.invalidateQueries({
          queryKey: VESSEL_KEYS.GET_VESSELS(filters),
        });
      },
      onError: (err) => {
        console.error(
          `Error ${vesselId ? "updating" : "creating"} vessel:`,
          err
        );
        error({
          message: `Error ${vesselId ? "updating" : "creating"} vessel`,
          description: err.message,
        });
      },
    });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const modalTitle = vesselData
    ? "Edit Vessel " + vesselData.name
    : "Add New Vessel";

  return (
    <Modal
      open={open}
      onCancel={() => {
        closeVesselModal();
        form.resetFields();
      }}
      footer={null}
      width={"70%"}
      title={<h3 className="text-lg font-medium">{modalTitle}</h3>}
      destroyOnHidden
    >
      <Spin spinning={isLoadingVessel}>
        <Form
          form={form}
          size="large"
          layout="vertical"
          requiredMark={CustomizeRequiredMark}
          onFinish={onFinish}
        >
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Please enter the name" }]}
            >
              <Input placeholder="e.g. SPR-2" />
            </Form.Item>
            <Form.Item
              label="Type"
              name="vesselTypeId"
              rules={[{ required: true, message: "Please select a type" }]}
            >
              <Select
                placeholder="Select a type"
                options={vesselTypes?.items.map((type) => ({
                  label: type.name,
                  value: type.id,
                }))}
              />
            </Form.Item>

            <Form.Item label="License Plate" name="licensePlate">
              <Input placeholder="e.g. NYK-1234" />
            </Form.Item>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <Form.Item label="Status" name="vesselStatus">
                <Select
                  placeholder="Select a status"
                  options={Object.values(VesselStatus).map((status) => ({
                    label: capitalize(status),
                    value: status,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Maintenance" name="maintenance">
                <Select
                  placeholder="Select a maintenance status"
                  options={Object.values(Maintenance).map((status) => ({
                    label: capitalize(status),
                    value: status,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Active"
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Form.Item>
            </div>
          </div>

          <h4 className="font-medium mb-2 mt-4 text-gray-700">Dimensions</h4>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Form.Item label="Box Length (inches)" name="boxLength">
              <InputNumber className="w-full" placeholder="0" type="number" />
            </Form.Item>
            <Form.Item label="Door Width (inches)" name="doorWidth">
              <InputNumber className="w-full" placeholder="0" type="number" />
            </Form.Item>
            <Form.Item label="Door Height (inches)" name="doorHeight">
              <InputNumber className="w-full" placeholder="0" type="number" />
            </Form.Item>
            <Form.Item label="Internal Height (inches)" name="internalHeight">
              <InputNumber className="w-full" placeholder="0" type="number" />
            </Form.Item>
            <Form.Item label="Overall Length (feet)" name="overallLength">
              <InputNumber
                className="w-full"
                placeholder="0.0"
                type="number"
                step="0.1"
              />
            </Form.Item>
            <Form.Item label="Overall Height (feet)" name="overallHeight">
              <InputNumber
                className="w-full"
                placeholder="0.0"
                type="number"
                step="0.1"
              />
            </Form.Item>
          </div>

          <h4 className="font-medium mb-2 mt-4 text-gray-700">Pricing</h4>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Form.Item label="Hourly Rate ($)" name="hourlyRate">
              <InputNumber
                className="w-full"
                placeholder="0.00"
                type="number"
                step="0.01"
                min={0}
                prefix="$"
              />
            </Form.Item>
            <Form.Item label="Direct Cost ($)" name="directCost">
              <InputNumber
                className="w-full"
                placeholder="0.00"
                type="number"
                step="0.01"
                min={0}
                prefix="$"
              />
            </Form.Item>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <Button
              onClick={closeVesselModal}
              className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              loading={isLoading}
            >
              {vesselId ? "Save Changes" : "Add Vessel"}
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
